import React from 'react';
import { CollapsibleSection } from '../../collapsible-section/CollapsibleSection';

export type PaymentOption = {
  id?: string;
  key?: string;
  value: string;
};

export type PriceBreakdownLine = {
  label: string;
  amount: string;
};

export type PaymentSectionData = {
  showPayment?: boolean;
  paymentMethodLabel?: string;
  selectedPaymentLabel?: string;
  paymentOptions?: PaymentOption[];
  paymentDropdownOpen?: boolean;
  onTogglePaymentDropdown?: () => void;
  onSelectPayment?: (option: PaymentOption) => void;
  showStartDate?: boolean;
  startDateLabel?: string;
  startDateValue?: string;
  minStartDate?: string;
  maxStartDate?: string;
  onStartDateChange?: (value: string) => void;
  showPriceBreakdown?: boolean;
  priceBreakdownLines?: PriceBreakdownLine[];
};

type Props = Readonly<{
  data: PaymentSectionData;
}>;

export function StartDateSection({ data }: Props) {
  const {
    showStartDate,
    startDateLabel,
    startDateValue,
    minStartDate,
    maxStartDate,
    onStartDateChange,
  } = data;

  if (!showStartDate) {
    return null;
  }

  return (
    <div className="content-group">
      <ul className="list no-border-bottom decrease-height">
        <li>
          <div className="list-text-left">
            <p>{startDateLabel}</p>
          </div>
          <div className="list-text-right">
            <p>
              <input
                type="date"
                className="start-date-picker"
                aria-label={startDateLabel}
                value={startDateValue || ''}
                min={minStartDate}
                max={maxStartDate}
                onChange={(event) => onStartDateChange?.(event.target.value)}
              />
            </p>
          </div>
        </li>
      </ul>
    </div>
  );
}

function PriceBreakdownGroup({ data }: Props) {
  const { showPriceBreakdown, priceBreakdownLines = [] } = data;

  if (!showPriceBreakdown || priceBreakdownLines.length === 0) {
    return null;
  }

  return (
    <div className="content-group-extra booking-purchase-price-breakdown">
      <ul className="list no-bottom-border">
        {priceBreakdownLines.map((line) => (
          <li key={`${line.label}:${line.amount}`}>
            <div className="list-text-left">
              <p>{line.label}</p>
            </div>
            <div className="list-text-right">
              <p>{line.amount}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function hasPaymentOption(data: PaymentSectionData) {
  return Boolean(data.showPayment && data.selectedPaymentLabel);
}

function PaymentGroup({ data }: Props) {
  if (!hasPaymentOption(data)) {
    return null;
  }

  return (
    <div className="content-group-extra">
      <PaymentOptionRow data={data} />
    </div>
  );
}

export function PaymentSection({ data }: Props) {
  if (!data) {
    return null;
  }

  return (
    <>
      <PriceBreakdownGroup data={data} />
      <PaymentGroup data={data} />
    </>
  );
}

export function PaymentOptionRow({ data }: Props) {
  if (!data || !hasPaymentOption(data)) {
    return null;
  }

  const {
    paymentMethodLabel,
    selectedPaymentLabel,
    paymentOptions = [],
    paymentDropdownOpen = false,
    onTogglePaymentDropdown,
    onSelectPayment,
  } = data;

  const canTogglePayment = paymentOptions.length > 1;
  const options =
    paymentOptions.length > 0
      ? paymentOptions
      : [{ value: selectedPaymentLabel || '' }];

  return (
    <CollapsibleSection
      data={{
        label: paymentMethodLabel || 'Payment method',
        expanded: canTogglePayment ? paymentDropdownOpen : undefined,
        onToggle: onTogglePaymentDropdown,
        expandable: canTogglePayment,
        id: 'class-booking-payment-options-toggle',
      }}
    >
      <PaymentOptions
        options={options}
        selectedLabel={selectedPaymentLabel}
        onSelect={canTogglePayment ? onSelectPayment : undefined}
      />
    </CollapsibleSection>
  );
}

function SelectedPaymentTick() {
  return (
    <span
      className="icon icon-confirmation collapsible-section__option-tick"
      aria-hidden="true"
      title="Selected"
    />
  );
}

function PaymentOptionButton({
  option,
  selected,
  onSelect,
}: Readonly<{
  option: PaymentOption;
  selected: boolean;
  onSelect: (option: PaymentOption) => void;
}>) {
  return (
    <button
      type="button"
      id={option.id ? `class-booking-payment-option-${option.id}` : undefined}
      className="collapsible-section__option"
      aria-pressed={selected}
      onClick={() => onSelect(option)}
    >
      {option.value}
      {selected ? <SelectedPaymentTick /> : null}
    </button>
  );
}

function PaymentOptions({
  options,
  selectedLabel,
  onSelect,
}: Readonly<{
  options: PaymentOption[];
  selectedLabel?: string;
  onSelect?: (option: PaymentOption) => void;
}>) {
  const selectable = Boolean(onSelect);

  return (
    <ul className="collapsible-section__options">
      {options.map((option) => {
        const selected = option.value === selectedLabel;

        return (
          <li key={option.id || option.value}>
            {selectable && onSelect ? (
              <PaymentOptionButton
                option={option}
                selected={selected}
                onSelect={onSelect}
              />
            ) : (
              <span className="collapsible-section__option">{option.value}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
